import React, { useEffect, useRef, useState } from "react";
import styles from "./Categories.module.scss";
import { Helmet } from "react-helmet";
import { CategoriesService } from "../../services/categories.service";
import {
  CategorisDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { message, Modal, Popconfirm, Spin } from "antd";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { useFormik } from "formik";
import * as Yup from "yup";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import { LanguageService } from "../../services/languages.service";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { FaPlus } from "react-icons/fa";
import ImageUploadInput from "./ImageUploadInput";

const Categories = () => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [categories, setCategories] = useState<CategorisDataType[]>([]);
  const [childCategories, setChildCategories] = useState<
    Record<string, CategorisDataType[]>
  >({});
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [langs, setLangs] = useState<languagesDataType[]>([]);

  const [primaryLangCode, setPrimaryLangCode] = useState<string>("UZ");
  const [langCode, setLangCode] = useState("UZ");

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(
    null
  );
  const [parentCategoryId, setParentCategoryId] = useState<number | null>(null);
  const [childCategoriesLoading, setChildCategoriesLoading] = useState<
    Record<string, boolean>
  >({});
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);

  const initialCategoryValues = useRef({
    name: "",
    translations: [] as { lang_code: string; name: string }[],
    image: "",
  });

  useEffect(() => {
    LanguageService.getAllLanguages().then((response) => {
      const data: languagesDataType[] = response?.data ?? [];
      setLangs(data);

      const foundUZ = data.find(
        (l) =>
          String(
            (l as any).code ?? (l as any).lang_code ?? ""
          ).toUpperCase() === "UZ"
      )
        ? (
            data.find(
              (l) =>
                String(
                  (l as any).code ?? (l as any).lang_code ?? ""
                ).toUpperCase() === "UZ"
            ) as any
          ).code ??
          (
            data.find(
              (l) =>
                String(
                  (l as any).code ?? (l as any).lang_code ?? ""
                ).toUpperCase() === "UZ"
            ) as any
          ).lang_code
        : null;

      const firstCode =
        (data?.[0] as any)?.code ?? (data?.[0] as any)?.lang_code ?? "UZ";

      const selectedPrimary = foundUZ ?? firstCode ?? "UZ";
      setPrimaryLangCode(selectedPrimary);
      setLangCode(selectedPrimary);
    });
  }, []);

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getChildCategory = (parentId: number) => {
    if (childCategories[parentId]) return;

    setChildCategoriesLoading((prev) => ({
      ...prev,
      [parentId]: true,
    }));

    CategoriesService.getCategoryChild(parentId)
      .then((response) => {
        if (response?.data) {
          setChildCategories((prev) => ({
            ...prev,
            [parentId]: response?.data,
          }));
        }
      })
      .finally(() => {
        setChildCategoriesLoading((prev) => ({
          ...prev,
          [parentId]: false,
        }));
      });
  };

  useEffect(() => {
    setLoading(true);
    CategoriesService.getAllCategoreis()
      .then((response) => {
        if (Array.isArray(response?.data)) {
          setCategories(response?.data);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const { handleSubmit, values, resetForm, touched, errors, setFieldValue } =
    useFormik({
      initialValues: {
        name: "",
        translations: [] as { lang_code: string; name: string }[],
        image: "" as string, // API üçün (data:image/png;base64,...)
        imagePreview: "" as string, // UI üçün
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Kateqoriya adı tələb olunur"),
      }),
      onSubmit: async (vals) => {
        try {
          setLoading(true);

          const primaryTranslation = {
            lang_code: String(primaryLangCode).toLowerCase(),
            name: vals.name,
          };

          const otherTranslations = (vals.translations || [])
            .map((t) => ({
              ...t,
              lang_code: String(t.lang_code).toLowerCase(),
            }))
            .filter(
              (t) => t.lang_code !== String(primaryLangCode).toLowerCase()
            );

          const translationsPayload = [
            primaryTranslation,
            ...otherTranslations,
          ];

          const basePayload = {
            parent_id: parentCategoryId || null,
            name: vals.name,
            image: vals.image || null,
            is_visible: true,
            translations: translationsPayload,
          };

          if (isEditing) {
            const isEqual =
              JSON.stringify(vals.name) ===
                JSON.stringify(initialCategoryValues.current.name) &&
              JSON.stringify(vals.translations) ===
                JSON.stringify(initialCategoryValues.current.translations) &&
              JSON.stringify(vals.image) ===
                JSON.stringify(initialCategoryValues.current.image);

            if (isEqual) {
              message.info("Dəyişiklik edilmədi.");
            } else {
              const response = await CategoriesService.updateCategory({
                ...basePayload,
                id: editingCategoryId,
              });

              if (response.status === 201 || response.status === 200) {
                message.success("Kateqoriya uğurla yeniləndi!");
                setIsModalVisible(false);
                resetForm();

                const updatedData = await CategoriesService.getAllCategoreis();
                setCategories(updatedData?.data || []);

                const parentId = response?.data?.parent_id;
                if (parentId) {
                  const childResp = await CategoriesService.getCategoryChild(
                    parentId
                  );
                  setChildCategories((prev) => ({
                    ...prev,
                    [parentId]: childResp?.data || [],
                  }));
                }
              } else {
                message.error(
                  // @ts-ignore
                  response.errorMessage || "Kateqoriyanı yeniləmək alınmadı."
                );
              }
            }
          } else {
            const response = await CategoriesService.addCategory(basePayload);

            if (response.status === 201 || response.status === 200) {
              message.success("Kateqoriya uğurla əlavə edildi!");
              setIsModalVisible(false);
              resetForm();

              const newId = response?.data?.data?.id ?? response?.data?.id;

              if (parentCategoryId) {
                const newCategory: CategorisDataType = {
                  id: newId,
                  parent_id: parentCategoryId,
                  name: vals.name,
                  image: vals.imagePreview,
                  translations: translationsPayload as any,
                  is_parent: false,
                } as any;

                setChildCategories((prevChildCategories: any) => ({
                  ...prevChildCategories,
                  [parentCategoryId]: [
                    ...(prevChildCategories[parentCategoryId] || []),
                    newCategory,
                  ],
                }));

                setCategories((prevCategories) =>
                  prevCategories.map((category) =>
                    category?.id === parentCategoryId
                      ? { ...category, is_parent: true }
                      : category
                  )
                );

                setExpandedRows((prev) => ({
                  ...prev,
                  [parentCategoryId]: true,
                }));
              } else {
                setCategories((prevCategories) => [
                  ...prevCategories,
                  {
                    id: newId,
                    parent_id: null,
                    name: vals.name,
                    image: vals.imagePreview,
                    translations: translationsPayload as any,
                  } as CategorisDataType,
                ]);
              }
            } else {
              message.error(
                // @ts-ignore
                response.errorMessage || "Kateqoriya əlavə etmək alınmadı."
              );
            }
          }
        } catch (error: any) {
          console.error(error);
          message.error(error.message || "Əməliyyat uğursuz oldu.");
        } finally {
          setLoading(false);
        }
      },
    });

  const handleTranslationChange = (value: string) => {
    if (langCode === primaryLangCode) {
      setFieldValue("name", value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) =>
          String(t.lang_code).toUpperCase() === String(langCode).toUpperCase()
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          name: value,
        };
      } else {
        updatedTranslations.push({ lang_code: langCode, name: value });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };

  const handleDeleteClick = async (record: CategorisDataType) => {
    try {
      const response = await CategoriesService.deleteCategory(record.id);

      if (response.status === 200) {
        setCategories((prevData) =>
          prevData.filter((item) => item.id !== record.id)
        );

        setChildCategories((prevChildCategories) => {
          const updatedChildCategories = { ...prevChildCategories };

          if (updatedChildCategories[record.id]) {
            delete updatedChildCategories[record.id];
          }

          Object.keys(updatedChildCategories).forEach((key) => {
            updatedChildCategories[key] = updatedChildCategories[key].filter(
              (childCategory) => childCategory.id !== record.id
            );
          });

          return updatedChildCategories;
        });

        if (record.parent_id) {
          const resp = await CategoriesService.getCategoryChild(
            record.parent_id
          );
          const updatedChildCategories = resp?.data;

          setChildCategories((prevChildCategories) => ({
            ...prevChildCategories,
            [record.parent_id as any]: updatedChildCategories,
          }));

          const isParent = updatedChildCategories.length > 0;

          setCategories((prevCategories) =>
            prevCategories.map((category) =>
              category.id === record.parent_id
                ? { ...category, is_parent: isParent }
                : category
            )
          );

          setExpandedRows((prev) => ({
            ...prev,
            [record.parent_id as any]:
              isParent && prev[record.parent_id as any],
          }));
        }

        message.success("Kateqoriya uğurla silindi!");
      } else {
        // @ts-ignore
        message.error(response.errorMessage || "Silmek alinmadi");
      }
    } catch (error) {
      message.error("Kateqoriyanı silmək alınmadı.");
    }
  };

  const handleAddClick = () => {
    setParentCategoryId(null);
    setIsModalVisible(true);
    resetForm();
    setIsEditing(false);
    setLangCode(primaryLangCode);

    initialCategoryValues.current = {
      name: "",
      translations: [],
      image: "",
    };
  };

  const handleChildAddClick = (parentId: number) => {
    setParentCategoryId(parentId);
    setIsEditing(false);
    setIsModalVisible(true);
    resetForm();
    setLangCode(primaryLangCode);

    initialCategoryValues.current = {
      name: "",
      translations: [],
      image: "",
    };
  };

  const handleEditClick = (category: CategorisDataType) => {
    setEditingCategoryId(category.id);
    setIsEditing(true);
    setIsModalVisible(true);

    setParentCategoryId(category.parent_id ?? null);

    const primaryTr = category.translations?.find(
      (t: any) =>
        String(t.lang_code).toUpperCase() ===
        String(primaryLangCode).toUpperCase()
    );

    setFieldValue("name", primaryTr?.name ?? category.name ?? "");
    setFieldValue("translations", category.translations || []);

    if (category.image) {
      setFieldValue("image", category.image);
      setFieldValue("imagePreview", category.image);
    } else {
      setFieldValue("image", "");
      setFieldValue("imagePreview", "");
    }

    setLangCode(primaryLangCode);

    initialCategoryValues.current = {
      name: primaryTr?.name ?? category.name ?? "",
      translations: category.translations || [],
      image: category.image || "",
    };
  };

  const renderCategories = (parentId: any | null, depth: number = 0) => {
    const parentCategories = categories
      .filter((category) => category.parent_id === parentId)
      .concat(childCategories[parentId] || []);

    if (!parentCategories.length) return null;

    return (
      <table className={styles.nestedTable}>
        <thead>
          <tr>
            <th></th>
            <th>№</th>
            <th>Kateqoriya adı</th>
            <th>Şəkil</th>
            <th>
              <div className={styles.settings}>
                <i className="fa-solid fa-gear"></i>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {parentCategories.map((category, index) => {
            const categoryKey = `${category.id}`;
            const isExpanded = expandedRows[categoryKey];
            const isLoading = childCategoriesLoading[category?.id];

            const hasChildren = childCategories[category.id]?.length > 0;
            const showIcon =
              (category.parent_id === null ||
                category.is_parent ||
                hasChildren) &&
              !(
                childCategories[category.id] &&
                childCategories[category.id].length === 0
              );
            const alwaysShowIcon = category.parent_id === null;

            return (
              <React.Fragment key={category.id}>
                <tr
                  onMouseEnter={() => setHoveredRowId(category.id)}
                  onMouseLeave={() => setHoveredRowId(null)}
                >
                  <td>
                    {showIcon || alwaysShowIcon ? (
                      <div className={styles.expand_icons}>
                        <span
                          className={`${styles.toggleIcon} ${
                            isExpanded ? styles.open : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(categoryKey);
                            if (!isExpanded) {
                              getChildCategory(category.id);
                            }
                          }}
                        >
                          {isExpanded ? "-" : "+"}
                        </span>
                      </div>
                    ) : (
                      <span></span>
                    )}
                  </td>

                  <td>{index + 1}</td>
                  <td>{category.name}</td>

                  <td>
                    {category.image ? (
                      <img
                        src={category.image}
                        alt={category.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      <span style={{ color: "#999" }}>Şəkil yoxdur</span>
                    )}
                  </td>

                  <td>
                    <div
                      className={`${styles.actions_icons} ${
                        hoveredRowId === category.id ||
                        category.parent_id === null
                          ? styles.visible
                          : styles.unvisible
                      }`}
                    >
                      <div
                        className={styles.edit}
                        onClick={() => handleEditClick(category)}
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </div>
                      <Popconfirm
                        title="Bu kateqoriyanı siləcəyinizə əminsiniz?"
                        onConfirm={() => handleDeleteClick(category)}
                        onCancel={() =>
                          message.info("Silinmə əməliyyatı ləğv edildi.")
                        }
                        okText="Bəli"
                        cancelText="Xeyr"
                        placement="topRight"
                      >
                        <div className={styles.delete}>
                          <i className="fa-solid fa-trash"></i>
                        </div>
                      </Popconfirm>
                      <div
                        className={styles.add}
                        onClick={() => handleChildAddClick(category.id)}
                      >
                        <i className="fa-solid fa-square-plus"></i>
                      </div>
                    </div>
                  </td>
                </tr>

                {isExpanded && (
                  <tr>
                    <td colSpan={5}>
                      {isLoading ? (
                        <div
                          style={{ padding: "30px 0", marginLeft: "-120px" }}
                        >
                          <Spin size="large" />
                        </div>
                      ) : (
                        renderCategories(category.id, depth + 1)
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div id={styles.categories_page}>
      <Helmet>
        <title>Orhan Door | Kateqoriyalar</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <h1 className={styles.page_title}>Kateqoriyalar</h1>
          <div className={styles.categories}>
            <div className={styles.table_head}>
              <MainButton onClick={handleAddClick}>
                <FaPlus size={20} />
              </MainButton>
            </div>
            <div className={styles.categories_wrapper}>
              <div className={styles.category}>
                <table className={styles.mainTable}>
                  <tbody>{renderCategories(null)}</tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          isEditing ? "Kateqoriyaya düzəliş edin" : "Yeni Kateqoriya əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        centered
        style={{ minWidth: "340px" }}
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />

          <div className="form_input">
            <label className="label" htmlFor="name">
              Kateqoriya adı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                name="name"
                id="name"
                placeholder="Kateqoriya adı"
                value={
                  langCode === primaryLangCode
                    ? values.name
                    : values.translations.find(
                        (t) =>
                          String(t.lang_code).toUpperCase() ===
                          String(langCode).toUpperCase()
                      )?.name || ""
                }
                onChange={(e) => handleTranslationChange(e.target.value)}
              />
              <div className="focus_border"></div>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>

          <ImageUploadInput
            label="Kateqoriya şəkli"
            value={values.imagePreview}
            onChange={(file) => {
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUrl = reader.result as string;
                  setFieldValue("image", dataUrl);
                  setFieldValue("imagePreview", dataUrl);
                };
                reader.readAsDataURL(file);
              } else {
                setFieldValue("image", "");
                setFieldValue("imagePreview", "");
              }
            }}
          />

          <MainButton full type="submit">
            {isEditing ? "Redaktə edin" : "Əlavə edin"}
          </MainButton>
        </form>
      </Modal>
    </div>
  );
};

export default Categories;
